import type { LegendItem } from "@typings/components/map";
import Map from "@commons/Map";
import Popup from "./Popup";
import { usePage } from "@hooks/usePage";
import { CircularProgress } from "@mui/material";
import { useGetIndividualsGeoJson } from "@services/individuals/getGeoJson";
import { toast } from "react-toastify";
import type { IndividualGeoJsonProperties } from "@services/individuals/getGeoJson/type";
import MapFilter from "./MapFilter";
import { useCallback, useEffect, useState } from "react";
import type { IndividualsFilters } from "@typings/entities/individuals/table";
import { formatDateTimeForAPI } from "@utils/formatters";
import { defaultFilters } from "@typings/entities/individuals/filter";
import { AppError } from "@utils/errors/appError";
import { mapColorToIndividuals } from "@utils/translator/individuals";
import { usePermissions } from "@hooks/usePermissions";

const LEGENDS = [
  { label: "Individuo com GPS", color: "bg-blue-500" },
  { label: "Individuo solto sem GPS", color: "bg-gray-500" },
] as LegendItem[];

function MapView() {
  const { openModal } = usePage();
  const [filters, setFilters] = useState<IndividualsFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<IndividualsFilters>(defaultFilters);
  const { can } = usePermissions();

  const { data, isLoading, error } = useGetIndividualsGeoJson(appliedFilters);

  const processedData = data ? mapColorToIndividuals(data) : null;

  const handleFilterChange = useCallback(
    (field: keyof IndividualsFilters, value: string) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    const applyFilters = { ...filters };
    applyFilters.createdAtStart = formatDateTimeForAPI(filters.createdAtStart);
    applyFilters.createdAtEnd = formatDateTimeForAPI(filters.createdAtEnd);
    applyFilters.releasedAtStart = formatDateTimeForAPI(
      filters.releasedAtStart,
    );
    applyFilters.releasedAtEnd = formatDateTimeForAPI(filters.releasedAtEnd);

    setAppliedFilters(applyFilters);
  }, [filters]);

  useEffect(() => {
    if (error) {
      const isAppError = error instanceof AppError;

      const title = isAppError
        ? error.message
        : "Erro ao carregar os indivíduos! Tente novamente!";
      toast.error(title, { autoClose: 6000 });
    }
  }, [error]);

  useEffect(() => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  }, []);

  const canCreateIndividual = can("create", "individuals");

  return (
    <>
      <MapFilter onChange={handleFilterChange} onApply={handleApplyFilters} />

      <div className="w-full h-[calc(100vh-12rem)] flex flex-col pt-2 gap-6">
        <div className="flex-1 bg-white rounded-lg shadow-md p-4 min-h-0">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Map.Root>
                {canCreateIndividual && (
                  <Map.Button onClick={() => openModal("newIndividual")}>
                    Novo Indivíduo
                  </Map.Button>
                )}
                <Map.Draw<IndividualGeoJsonProperties>
                  geoJsonData={processedData}
                  popupComponent={Popup}
                  canCreate={canCreateIndividual}
                  modalToOpen="newIndividual"
                  coordinatesButtonLabel="Novo Indivíduo"
                />
                <Map.Footer legends={LEGENDS} />
              </Map.Root>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default MapView;
